import { memo, useCallback } from "react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { Hourglass, XCircle } from "lucide-react";
import { formatDuration } from "@/components/agent/display/utils/formatters";
import { GenieAvatar } from "@/components/GenieAvatar";
import { cn } from "@/lib/utils";
import { Conversation } from "@/types/conversation/conversation";
import { TranscriptCopyButton } from "./details/TranscriptCopyButton";
import {
  ConversationAttentionPill,
  ConversationAnnotationPills,
  ConversationChannelPill,
  extractConversationSignals,
} from "./conversationListShared";

interface ConversationTableViewProps {
  conversations: Conversation[];
  onViewDetails: (conversation: Conversation) => void;
}

/**
 * Dense scannable view — one row per conversation.
 *
 * Genie cell is width-capped so it doesn't absorb every spare pixel on
 * wide screens; the "About" column holds the primary topic / summary
 * preview and is the only flexible-width column, so leftover horizontal
 * space turns into useful context instead of empty whitespace.
 */
export const ConversationTableView = memo(
  ({ conversations, onViewDetails }: ConversationTableViewProps) => {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1060px] table-fixed text-left">
            <thead>
              <tr className="bg-[#F9FAFC] border-b border-gray-200 text-[11px] uppercase tracking-wide text-gray-500">
                <th className="px-5 py-3 font-semibold w-[300px]">Who</th>
                <th className="px-3 py-3 font-semibold">About</th>
                <th className="px-3 py-3 font-semibold whitespace-nowrap w-[68px]">When</th>
                <th className="px-3 py-3 font-semibold whitespace-nowrap w-[105px]">Channel</th>
                <th className="px-3 py-3 font-semibold whitespace-nowrap w-[105px]">Attention</th>
                <th className="px-3 py-3 font-semibold whitespace-nowrap w-[110px]">Duration</th>
                <th className="px-3 py-3 font-semibold whitespace-nowrap w-[160px]">
                  <span className="sr-only">Transcript</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {conversations.map((conversation) => (
                <ConversationTableRow
                  key={conversation.id}
                  conversation={conversation}
                  onViewDetails={onViewDetails}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  },
);

ConversationTableView.displayName = "ConversationTableView";

interface ConversationTableRowProps {
  conversation: Conversation;
  onViewDetails: (conversation: Conversation) => void;
}

const ConversationTableRow = memo(
  ({ conversation, onViewDetails }: ConversationTableRowProps) => {
    const signals = extractConversationSignals(conversation);
    const isViewed = conversation.viewed ?? false;
    const agentName = conversation.agent?.name || "Unknown Agent";
    const endedEarly = signals.statusBadge?.tone === "ended";

    const handleClick = useCallback(() => {
      onViewDetails(conversation);
    }, [conversation, onViewDetails]);

    return (
      <tr
        onClick={handleClick}
        className={cn(
          "border-b border-gray-100 last:border-b-0 cursor-pointer transition-colors hover:bg-[#F9FAFC]",
          signals.isUrgent ? "bg-genie-secondary/[0.06]" : !isViewed && "bg-[#4E9CFF]/[0.03]",
        )}
      >
        <td className={cn("px-5 py-2.5", signals.isUrgent && "shadow-[inset_3px_0_0_#FF4DB8]")}>
          <div className="flex items-center gap-3 overflow-hidden">
            <div className="flex-shrink-0">
              {signals.logoUrl ? (
                <img
                  src={signals.logoUrl}
                  alt={agentName}
                  className="w-7 h-7 object-contain rounded-full"
                  style={{ filter: `drop-shadow(0 0 4px ${signals.brandColor}30)` }}
                />
              ) : (
                <div className="w-7 h-7">
                  <GenieAvatar
                    size={28}
                    color={signals.brandColor}
                    secondaryColor={signals.secondaryColor}
                    gradientEnabled={signals.hasGradient}
                    gradientAngle={signals.gradientAngle}
                    customAvatarUrl={signals.logoUrl}
                  />
                </div>
              )}
            </div>
            {/* Two-line stack: caller name + status on top, genie name +
                annotation pills below — the two names no longer truncate
                against each other on a single line. */}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="min-w-0 truncate text-sm font-semibold text-gray-900">
                  <CopyableEmail email={signals.customerLabel ?? agentName} textClassName="truncate" />
                </span>
                {!isViewed && (
                  <span
                    className="flex-shrink-0 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded text-white"
                    style={{ background: signals.brandColor }}
                  >
                    New
                  </span>
                )}
                {endedEarly && (
                  <span
                    className="flex-shrink-0 inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded"
                    style={{ color: "#D6409F", background: "rgba(214,64,159,0.08)" }}
                  >
                    <XCircle className="w-2.5 h-2.5" />
                    Ended
                  </span>
                )}
                {!conversation.isSynced && (
                  <span
                    className="flex-shrink-0 inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded"
                    style={{
                      color: "#FFBB33",
                      background: "rgba(255,187,51,0.12)",
                    }}
                  >
                    <Hourglass className="w-2.5 h-2.5" />
                    Not analyzed
                  </span>
                )}
              </div>
              <div className="mt-0.5 flex items-center gap-1.5 empty:hidden">
                {signals.customerLabel && (
                  <span className="min-w-0 truncate text-xs text-gray-500">
                    {agentName}
                  </span>
                )}
                <ConversationAnnotationPills
                  conversationId={conversation.id}
                  dense
                  hideEmpty
                />
              </div>
            </div>
          </div>
        </td>
        <td className="px-3 py-2.5 text-sm text-gray-600">
          <AboutCell
            primaryTopic={signals.primaryTopic}
            summaryPreview={signals.summaryPreview}
          />
        </td>
        <td className="px-3 py-2.5 text-xs font-medium text-gray-600 whitespace-nowrap">
          {signals.hasValidTime ? (
            <span title={`${signals.timeAgo} at ${signals.exactTime}`}>
              {compactRelativeTime(signals.timeAgo)}
            </span>
          ) : (
            <span className="text-gray-400">—</span>
          )}
        </td>
        <td className="px-3 py-2.5 whitespace-nowrap">
          <ConversationChannelPill channel={signals.channel} />
        </td>
        <td className="px-3 py-2.5 whitespace-nowrap">
          <ConversationAttentionPill attention={signals.attentionBadge} />
        </td>
        <td className="px-3 py-2.5 text-sm text-gray-700 whitespace-nowrap">
          {signals.durationSeconds !== null
            ? formatDuration(signals.durationSeconds, true)
            : "—"}
        </td>
        <td className="px-3 py-2.5 whitespace-nowrap text-right">
          <TranscriptCopyButton conversation={conversation} compact />
        </td>
      </tr>
    );
  },
);

ConversationTableRow.displayName = "ConversationTableRow";

const AboutCell = ({
  primaryTopic,
  summaryPreview,
}: {
  primaryTopic: string | null;
  summaryPreview: string | null;
}) => {
  if (!primaryTopic && !summaryPreview) {
    return <span className="text-gray-400">—</span>;
  }

  // Gmail-style single line: "Topic — summary preview", truncated together.
  return (
    <div className="min-w-0 truncate text-[13px]">
      {primaryTopic && (
        <span className="font-medium text-gray-800">{primaryTopic}</span>
      )}
      {primaryTopic && summaryPreview && (
        <span className="text-gray-400"> — </span>
      )}
      {summaryPreview && (
        <span className="text-[12px] text-gray-500">{summaryPreview}</span>
      )}
    </div>
  );
};

const compactRelativeTime = (timeAgo: string) =>
  timeAgo
    .replace("less than a minute ago", "now")
    .replace(/^about /, "")
    .replace(/ minutes? ago$/, "m")
    .replace(/ hours? ago$/, "h")
    .replace(/ days? ago$/, "d")
    .replace(/ months? ago$/, "mo")
    .replace(/ years? ago$/, "y");
